import { useEffect, useState } from 'react';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

/**
 * usePushNotifications
 *
 * Requests browser push notification permission on mount (if not already decided).
 * Returns the current permission state and a manual re-request function.
 *
 * Usage:
 *   const { permission, requestPermission } = usePushNotifications();
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState<PermissionState>(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission as PermissionState;
  });

  useEffect(() => {
    if (permission === 'unsupported' || permission === 'denied') return;
    // Auto-request only once when status is still 'default'
    if (permission === 'default') {
      requestPermission();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestPermission = async () => {
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result as PermissionState);
    } catch {
      // Browsers that don't support the promise form
      Notification.requestPermission((result) => {
        setPermission(result as PermissionState);
      });
    }
  };

  return { permission, requestPermission };
}
